import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CustomizedCircularProgress,
  CustomizedTypography
} from '@components/Customized';

import { CircularProgressProps } from '@material-ui/core/CircularProgress';

import { formatFileSize } from '@utils/formatter';

import { useSendQuotationChatMessageMutation } from '@generated/graphql';

import axios, { CancelTokenSource } from 'axios';

import { CapaFileUpload } from '@api/rest';
import { clsx } from '@components/Provider';

interface Props extends CircularProgressProps {
  file: File;
  fileId: string;
  chatRoomId: string;
  isImage: boolean;
}

interface DefaultStatus {
  state: 'default' | 'complete' | 'abort';
  value: number;
  loaded: number;
  fildId: string;
  fileSize: number;
}

const defaultStatus: DefaultStatus = {
  fildId: '',
  state: 'default',
  value: 0,
  fileSize: 0,
  loaded: 0
};

const QueueItem = ({ chatRoomId, isImage, file, fileId }: Props) => {
  const [preview, setPreview] = useState('');

  const [status, setStatus] = useState<typeof defaultStatus>(() => {
    return { ...defaultStatus, fileId, fileSize: file.size };
  });

  const isCompleted = status.state === 'complete';

  const [sendFileUploadMessage] = useSendQuotationChatMessageMutation();

  const source = useRef<CancelTokenSource | null>(null);

  const sendFileMessage = useCallback(
    async (data: { fileId: string; fileName: string }) => {
      try {
        await sendFileUploadMessage({
          variables: {
            input: {
              chatRoomId: chatRoomId as string,
              files: [
                {
                  fileId: data.fileId,
                  fileName: data.fileName
                }
              ]
            }
          }
        });
      } catch (e) {}
    },
    [sendFileUploadMessage, chatRoomId]
  );

  const onCancelUpload = useCallback(() => {
    if (isCompleted) return;

    if (source.current !== null) {
      source.current.cancel();

      setStatus((prev) => ({ ...prev, state: 'abort' }));
    }
  }, [isCompleted]);

  useEffect(() => {
    if (isCompleted) return;
    // File Upload IIFE
    (async () => {
      const formData = new FormData();

      source.current = axios.CancelToken.source();

      formData.append('file', file, file.name);

      const {
        data: { data }
      } = await CapaFileUpload(
        { formData: formData },
        {
          cancelToken: source.current.token,
          onUploadProgress: (e) => {
            const percent = (e.loaded * 100) / e.total;
            setStatus((prev) => ({
              ...prev,
              value: percent,
              loaded: e.loaded
            }));
          }
        }
      );

      await sendFileMessage({ ...data });

      setStatus((prev) => ({ ...prev, state: 'complete' }));
    })();

    return () => source.current?.cancel();
  }, [isImage, chatRoomId, file, isCompleted, sendFileMessage]);

  useEffect(() => {
    if (isCompleted) return;

    if (isImage) {
      const reader = new FileReader();

      reader.onloadend = (e) => {
        setPreview(e.target?.result as string);
      };

      reader.readAsDataURL(file);
    }
  }, [isImage, isCompleted, file]);

  if (status.state === 'complete' || status.state === 'abort') return null;

  return (
    <div
      className={clsx('queue-item', {
        image: isImage
      })}
    >
      {isImage && (
        <>
          <div
            className="bg-img"
            style={{
              backgroundImage: `url(${preview})`
            }}
          />

          <div className="bg-img overlay" />

          <div className="floating">
            <CustomizedCircularProgress
              color="gray"
              variant="cancel"
              value={status.value}
            />
            <CustomizedTypography
              className="textWhtie uploading"
              variant="subtitle2"
              color="inherit"
              fontWeight="normal"
            >
              업로드 중
            </CustomizedTypography>
            <CustomizedTypography
              className="textWhtie"
              variant="caption"
              color="inherit"
              fontWeight="normal"
            >
              {formatFileSize(status.loaded)} /&nbsp;
              {formatFileSize(file.size)}
            </CustomizedTypography>
          </div>
        </>
      )}

      {!isImage && (
        <>
          <div className="queue-file-name">
            <CustomizedTypography
              className="name"
              variant="body1"
              color="textPrimary"
              fontWeight="500"
              gutterBottoms={4}
            >
              {file.name}
            </CustomizedTypography>

            <CustomizedCircularProgress
              value={status.value ?? 0}
              variant="cancel"
              color="primary"
              onCancel={onCancelUpload}
            />
          </div>
          <CustomizedTypography
            color="textSecondary"
            variant="caption"
            gutterBottoms={16}
          >
            {formatFileSize(file.size)}
          </CustomizedTypography>

          <div className="queue-cta">
            {status.state === 'default' ? (
              <CustomizedTypography
                onClick={onCancelUpload}
                className="abort"
                color="error"
                variant="body2"
                fontWeight="500"
              >
                취소
              </CustomizedTypography>
            ) : (
              ''
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QueueItem;
